import { useCart } from "../lib/cartStore";
import { formatCents, formatWholeDollars, getProductTiers, isStudentProduct, isVceSchoolLicence } from "../lib/products";

interface CartSidebarProps {
  onSubmit: () => void;
  onCancel: () => void;
  submitting: boolean;
  isPunchOut: boolean;
}

export function CartSidebar({
  onSubmit,
  onCancel,
  submitting,
  isPunchOut,
}: CartSidebarProps) {
  const {
    entries,
    isEmpty,
    updateQuantity,
    removeFromCart,
    clearCart,
    getTotals,
    getCartItems,
    quoteContext,
  } =
    useCart();

  const totals = getTotals();
  const manualDiscounts = quoteContext?.manualDiscounts || [];
  const validManualDiscounts = manualDiscounts.filter((discount) => discount.amountCents > 0);
  const manualDiscountTotal = validManualDiscounts.reduce((sum, discount) => sum + discount.amountCents, 0);
  const adjustedSubtotalExGst = totals.subtotalExGstCents - manualDiscountTotal;
  const adjustedGst = Math.round(adjustedSubtotalExGst * 0.1);
  const adjustedTotal = adjustedSubtotalExGst + adjustedGst;

  function handleClearCart() {
    if (window.confirm("Are you sure you want to clear your cart?")) {
      clearCart();
    }
  }

  return (
    <div className="cart-sidebar">
      <div className="cart-header">
        <h2>Your Cart</h2>
        {entries.length > 0 && (
          <span className="cart-count">
            {entries.length} {entries.length === 1 ? "item" : "items"}
          </span>
        )}
      </div>

      {isEmpty ? (
        <div className="cart-empty">
          <p>Your cart is empty</p>
          <p className="cart-empty-hint">
            Add products from the catalog to get started.
          </p>
        </div>
      ) : (
        <>
          <div className="cart-items">
            {entries.map((entry) => {
              const tiers = getProductTiers(entry.group, entry.variantId);
              const initialTier = tiers.find((t) => t.tierType === "initial");
              const additionalTier = tiers.find((t) => t.tierType === "additional");
              const isStudent = isStudentProduct(entry.group);
              // VCE school licence: shown as one licence, never as its 99 seats, and not
              // adjustable. Keyed on the product alone — VCE is never offered in PunchOut,
              // so every real PunchOut row is untouched without a mode branch.
              const isLicence = isVceSchoolLicence(entry.group);

              const firstPrice = initialTier?.unitPriceExGstCents || 0;
              const additionalPrice = additionalTier?.unitPriceExGstCents || 0;
              const additionalCount = Math.max(0, entry.quantity - 1);
              const linePrice = firstPrice + (additionalPrice * additionalCount);

              // Get variant display name
              const variantName = entry.variantId
                ? entry.group.variants?.find((v) => v.id === entry.variantId)?.name
                : null;

              // Get students per block for display (student products)
              const initialStudents = initialTier?.studentsPerBlock || 0;
              const additionalStudents = additionalTier?.studentsPerBlock || 0;

              // Build quantity display
              let quantityDisplay: string;
              if (isLicence) {
                quantityDisplay = "School licence";
              } else if (isStudent) {
                const totalStudents = initialStudents + (additionalCount * additionalStudents);
                quantityDisplay = `${totalStudents} students`;
              } else {
                quantityDisplay = `${entry.quantity} ${entry.quantity === 1 ? "teacher" : "teachers"}`;
              }

              // Build pricing breakdown (whole dollars for scannability)
              let priceBreakdown: string;
              if (isLicence) {
                priceBreakdown = "Includes all teachers at your school";
              } else if (isStudent) {
                priceBreakdown = additionalCount > 0
                  ? `${formatWholeDollars(firstPrice)} (${initialStudents} included) + ${formatWholeDollars(additionalPrice)} × ${additionalCount} additional`
                  : `${formatWholeDollars(firstPrice)} (${initialStudents} students included)`;
              } else {
                priceBreakdown = additionalCount > 0
                  ? `${formatWholeDollars(firstPrice)} first + ${formatWholeDollars(additionalPrice)} × ${additionalCount}`
                  : `${formatWholeDollars(firstPrice)} first teacher`;
              }

              return (
                <div key={entry.groupId} className="cart-item">
                  <div className="cart-item-header">
                    <div className="cart-item-name">
                      {entry.group.name}
                      {variantName && (
                        <span className="cart-item-variant"> — {variantName}</span>
                      )}
                    </div>
                  </div>

                  <div className="cart-item-details">
                    <div className="cart-item-teachers">
                      {quantityDisplay}
                    </div>
                    <div className="cart-item-breakdown">{priceBreakdown}</div>
                    <div className="cart-item-line-total">
                      {formatCents(linePrice)} / year
                    </div>
                  </div>

                  <div className="cart-item-controls">
                    {!isLicence && (
                      <div className="quantity-stepper">
                        <button
                          type="button"
                          className="stepper-btn"
                          onClick={() =>
                            updateQuantity(entry.groupId, entry.quantity - 1)
                          }
                          disabled={submitting}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <span className="stepper-value">
                          {isStudent
                            ? initialStudents + (additionalCount * additionalStudents)
                            : entry.quantity}
                        </span>
                        <button
                          type="button"
                          className="stepper-btn"
                          onClick={() =>
                            updateQuantity(entry.groupId, entry.quantity + 1)
                          }
                          disabled={submitting}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      className="cart-item-remove"
                      onClick={() => removeFromCart(entry.groupId)}
                      disabled={submitting}
                      aria-label="Remove item"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bundle Discounts Section */}
          {(() => {
            const allItems = getCartItems();
            const discountItems = allItems.filter((item) => item.isDiscount);
            if (discountItems.length === 0) return null;

            return (
              <div className="cart-discounts">
                <div className="cart-discounts-header">Bundle Discounts Applied</div>
                {discountItems.map((discount, idx) => (
                  <div key={idx} className="cart-discount-line">
                    <span className="cart-discount-name">{discount.name}</span>
                    <span className="cart-discount-amount">
                      {formatCents(discount.unitPriceExGstCents)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          {validManualDiscounts.length > 0 && (
            <div className="cart-discounts">
              <div className="cart-discounts-header">Manual Discounts</div>
              {validManualDiscounts.map((discount, idx) => (
                <div key={`manual-${idx}`} className="cart-discount-line">
                  <span className="cart-discount-name">{discount.description || "Discount"}</span>
                  <span className="cart-discount-amount">
                    {formatCents(-discount.amountCents)}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="cart-totals">
            <div className="cart-total-row">
              <span>Subtotal</span>
              <span>{formatCents(totals.subtotalExGstCents)}</span>
            </div>
            {manualDiscountTotal > 0 && (
              <div className="cart-total-row">
                <span>Manual Discounts</span>
                <span>{formatCents(-manualDiscountTotal)}</span>
              </div>
            )}
            <div className="cart-total-row">
              <span>GST (10%)</span>
              <span>{formatCents(adjustedGst)}</span>
            </div>
            <div className="cart-total-row cart-total-final">
              <span>Total</span>
              <span>{formatCents(adjustedTotal)}</span>
            </div>
          </div>
        </>
      )}

      <div className="cart-actions">
        {isPunchOut ? (
          <>
            <button
              type="button"
              className="btn btn-primary btn-full"
              onClick={onSubmit}
              disabled={isEmpty || submitting}
            >
              {submitting ? "Submitting..." : "Finalise my order"}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-full"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-primary btn-full"
              onClick={onSubmit}
              disabled={isEmpty || submitting}
            >
              Proceed to checkout
            </button>
          </>
        )}
        {!isEmpty && (
          <button
            type="button"
            className="btn btn-text btn-full"
            onClick={handleClearCart}
            disabled={submitting}
          >
            Clear cart
          </button>
        )}
      </div>

    </div>
  );
}
